import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY } from "./nyHelpers";

const imageStyle = {
	width: 70,
	height: 70,
	minWidth: 70,
	objectFit: "cover",
	borderRadius: 10,
};

// Template82: Día 4 Central Park y Upper Manhattan (Page13). Contenido desde page.
const Template82 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		if (!text) return null;
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const title = page?.texts?.[0]?.content;
	const titleShort = page?.texts?.[1]?.content;
	const heroH1 = page?.texts?.[2]?.content;
	const gospelIntro = page?.texts?.[3]?.content;
	const gospelYellow = page?.texts?.[4]?.content;
	const gospelH3 = page?.texts?.[5]?.content;
	const gospelP = page?.texts?.[6]?.content;
	const sabiasQueGospel = parseSabiasQueItem(page?.texts?.[7]?.content);
	const sectionTitle = page?.texts?.[8]?.content;
	const bethTitle = page?.texts?.[9]?.content;
	const bethBody = page?.texts?.[10]?.content;
	const sabiasQueBeth = parseSabiasQueItem(page?.texts?.[11]?.content);
	const bowTitle = page?.texts?.[12]?.content;
	const bowBody = page?.texts?.[13]?.content;
	const strawTitle = page?.texts?.[14]?.content;
	const strawBody = page?.texts?.[15]?.content;
	const belvTitle = page?.texts?.[16]?.content;
	const belvBody = page?.texts?.[17]?.content;
	const sabiasQueBelv = parseSabiasQueItem(page?.texts?.[18]?.content);

	const centralParkSrc = getImageSrc("central-park");
	const gospelSrc = getImageSrc("gospel");
	const bethesdaSrc = getImageSrc("bethesda");
	const bowBridgeSrc = getImageSrc("bow-bridge");
	const imagineSrc = getImageSrc("imagine");
	const castleSrc = getImageSrc("castle");
	const logoSrc = getCanvasImageUrl("logo");

	const displayTitle = isSmallScreen && titleShort ? titleShort : title;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{displayTitle}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			{centralParkSrc && (
				<div data-block-id="image-0" style={{ position: "relative" }}>
					<img
						src={centralParkSrc}
						alt="Central Park"
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{heroH1 && (
						<h1
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: 32,
								position: "absolute",
								bottom: 0,
								margin: 10,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{heroH1}
						</h1>
					)}
				</div>
			)}

			{(gospelIntro || gospelH3 || gospelSrc) && (
				<div
					data-block-id="text-1"
					style={{
						display: "flex",
						gap: 7,
						padding: "10px 12px",
						position: "relative",
						flexDirection: "row-reverse",
					}}
				>
					{gospelSrc && (
						<div
							style={{
								float: "right",
								position: "relative",
								height: 174,
								marginLeft: 8,
								marginTop: -36,
							}}
						>
							<img
								src={gospelSrc}
								alt="Gospel"
								style={{ width: 100, marginLeft: 35, marginTop: 85 }}
							/>
							{gospelYellow && (
								<div
									style={{
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 0px 16px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: '"Poppins", sans-serif',
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 180,
										position: "absolute",
										top: 116,
										right: 10,
										transform: "translateY(-100%)",
									}}
								>
									{gospelYellow}
								</div>
							)}
						</div>
					)}
					<div style={{ flex: 1 }}>
						{gospelIntro && (
							<p
								style={{
									fontFamily: '"Poppins", sans-serif',
									fontSize: 13,
									color: "#444",
									margin: 0,
								}}
							>
								{gospelIntro}
							</p>
						)}
						{gospelH3 && (
							<h3
								style={{
									fontFamily: '"Poppins", sans-serif',
									fontSize: 14,
									fontWeight: 700,
									textTransform: "uppercase",
									marginBottom: 8,
									marginTop: 12,
									color: "#000",
								}}
							>
								{gospelH3}
							</h3>
						)}
						{gospelP && (
							<p
								style={{
									fontFamily: '"Poppins", sans-serif',
									fontSize: 13,
									color: "#444",
									margin: 0,
								}}
							>
								{gospelP}
							</p>
						)}
					</div>
					{sabiasQueGospel && (
						<div
							style={{
								position: "absolute",
								top: isSmallScreen ? 120 : 55,
								left: isSmallScreen ? "60%" : "45%",
							}}
						>
							<SabiasQueNY
								icono={sabiasQueGospel.icono}
								texto={sabiasQueGospel.texto}
							/>
						</div>
					)}
				</div>
			)}

			{sectionTitle && (
				<h3
					style={{
						fontFamily: '"Poppins", sans-serif',
						fontSize: 14,
						fontWeight: 700,
						textTransform: "uppercase",
						padding: "16px 12px 0",
						marginBottom: 8,
						marginTop: -20,
					}}
				>
					{sectionTitle}
				</h3>
			)}
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 8,
					padding: "0 12px",
				}}
			>
				{(bethTitle || bethBody || bethesdaSrc) && (
					<div data-block-id="text-2" style={{ display: "flex", gap: 16, position: "relative" }}>
						{bethesdaSrc && (
							<img src={bethesdaSrc} style={imageStyle} alt="Bethesda" />
						)}
						<div>
							{bethTitle && (
								<h4
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										fontWeight: 700,
										margin: 0,
									}}
								>
									{bethTitle}
								</h4>
							)}
							{bethBody && (
								<p
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										color: "#444",
										margin: 0,
									}}
								>
									{bethBody}
								</p>
							)}
						</div>
						{sabiasQueBeth && (
							<div
								style={{
									position: "absolute",
									top: isSmallScreen ? -70 : -40,
									left: "60%",
								}}
							>
								<SabiasQueNY
									icono={sabiasQueBeth.icono}
									texto={sabiasQueBeth.texto}
								/>
							</div>
						)}
					</div>
				)}
				{(bowTitle || bowBody || bowBridgeSrc) && (
					<div data-block-id="text-3" style={{ display: "flex", gap: 16 }}>
						{bowBridgeSrc && (
							<img src={bowBridgeSrc} style={imageStyle} alt="Bow Bridge" />
						)}
						<div>
							{bowTitle && (
								<h4
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										fontWeight: 700,
										margin: 0,
									}}
								>
									{bowTitle}
								</h4>
							)}
							{bowBody && (
								<p
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										color: "#444",
										margin: 0,
									}}
								>
									{bowBody}
								</p>
							)}
						</div>
					</div>
				)}
				{(strawTitle || strawBody || imagineSrc) && (
					<div data-block-id="text-4" style={{ display: "flex", gap: 16 }}>
						{imagineSrc && (
							<img
								src={imagineSrc}
								style={imageStyle}
								alt="Strawberry Fields"
							/>
						)}
						<div>
							{strawTitle && (
								<h4
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										fontWeight: 700,
										margin: 0,
									}}
								>
									{strawTitle}
								</h4>
							)}
							{strawBody && (
								<p
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										color: "#444",
										margin: 0,
									}}
								>
									{strawBody}
								</p>
							)}
						</div>
					</div>
				)}
				{(belvTitle || belvBody || castleSrc) && (
					<div
						data-block-id="text-5"
						style={{
							display: "flex",
							gap: 16,
							position: "relative",
							marginBottom: 12,
						}}
					>
						{castleSrc && (
							<img src={castleSrc} style={imageStyle} alt="Belvedere Castle" />
						)}
						<div>
							{belvTitle && (
								<h4
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										fontWeight: 700,
										margin: 0,
									}}
								>
									{belvTitle}
								</h4>
							)}
							{belvBody && (
								<p
									style={{
										fontFamily: '"Poppins", sans-serif',
										fontSize: 13,
										color: "#444",
										margin: 0,
									}}
								>
									{belvBody}
								</p>
							)}
						</div>
						{sabiasQueBelv && (
							<div style={{ position: "absolute", top: -25, left: "60%" }}>
								<SabiasQueNY
									icono={sabiasQueBelv.icono}
									texto={sabiasQueBelv.texto}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</Box>
	);
};

export default Template82;
